"use client";

import React, { useEffect, useState } from "react";
import { HomeIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import Image from "next/image";
import Link from "next/link";
import axios from "axios";
import {
  CmsBannerDetails,
  PageDetails,
  PinnedPage,
  Tags,
} from "@/interface/types";
import DOMPurify from "dompurify";
import CMSPageParseDescriptionExtend from "./parsePageDescExt";
import { useRouter } from "next/navigation";

const Details = () => {
  const [isPopupOpen, setPopupOpen] = useState({ open: false, code: "" });
  const [pageDetails, setPageDetails] = useState<PageDetails | null>(null);
  const [pinnedPageDetails, setPinnedPageDetails] =
    useState<PageDetails | null>(null);
  const [pinnedPages, setPinnedPages] = useState<PinnedPage[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [, setIsLoading] = useState<boolean>(false);
  const [bannerDetails, setBannerDetails] = useState<CmsBannerDetails | null>(
    null,
  );
  const [tagName, setTagName] = useState<Tags[]>([]);

  const [categorySlug, setCategorySlug] = useState<string>("");
  const [tagSlug, setTagSlug] = useState<string>("");
  const [pageSlug, setPageSlug] = useState<string>("");
  const [status, setStatus] = useState<number>(0);
  const [activePage, setActivePage] = useState<string>(pageSlug || "");

  const router = useRouter();

  const handlePageClick = async (pageName: string) => {
    setActivePage(pageName);
    setIsLoading(true);

    const matchPinnedPageSlug = pinnedPages.find(
      (pinPage) => pinPage.name === pageName,
    );

    if (!matchPinnedPageSlug) {
      setIsLoading(false);
      return;
    }

    const { slug } = matchPinnedPageSlug;

    try {
      const API_URL = `https://website-cms-backend-prod-154625965269.asia-south1.run.app/pages/?tag=${tagSlug}&page_slug=${slug}&category=${categorySlug}`;
      const response = await axios.get(API_URL);
      const { status, data } = response;

      if (status === 200) {
        setStatus(status);
        setPinnedPageDetails(data);
      } else if (status === 204) {
        setStatus(204);
        setPinnedPageDetails({
          abstract: "",
          active: false,
          content: "No content available",
          created_at: "",
          id: "",
          name: "",
          pinned_page: false,
          placeholder: "",
          published_on: "",
          subtitle: "",
          tags: "",
          title: "",
          updated: false,
          updated_at: "",
          video: "",
        });
      } else {
        console.warn("Failed to display pinned page details:");
      }
    } catch (error) {
      console.error(error);
    }

    const newPath = `/accounting/learn/${categorySlug}/${tagSlug}/${slug}`;
    router.push(newPath);
    window.location.href = newPath;

    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  };

  const closePopup = () => {
    setPopupOpen({ open: false, code: "" });
  };

  const fetchPageDetails = async () => {
    setIsLoading(true);
    if (!tagSlug || !pageSlug || !categorySlug) {
      setIsLoading(false);
      return;
    }

    try {
      const API_URL = `https://website-cms-backend-prod-154625965269.asia-south1.run.app/pages/?tag=${tagSlug}&page_slug=${pageSlug}&category=${categorySlug}`;
      const response = await axios.get(API_URL);
      const { status, data } = response;
      if (status === 200) {
        setIsLoading(false);
        setPageDetails(data);
      } else {
        console.warn("no page details available");
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const fetchTags = async (category: string) => {
    try {
      const response = await axios.get(
        `https://website-cms-backend-prod-154625965269.asia-south1.run.app/tags?category=${category}`,
      );
      const { status, data } = response;
      if (status === 200) {
        setTagName(data.Data);
      } else {
        console.warn("Failed to fetch tag name");
      }
    } catch (error) {
      console.error("tag failure", error);
    }
  };

  const fetchPinnedPages = async (category: string, tag: string) => {
    if (!category || !tag) {
      console.warn("Category or Tag is missing. Cannot fetch pinned pages.");
      return;
    }

    try {
      const response = await axios.get(
        `https://website-cms-backend-prod-154625965269.asia-south1.run.app/pinned_pages/?category=${category}&tag=${tag}`,
      );
      const { status, data } = response;
      if (status === 200) {
        setPinnedPages(data.Data);
      } else {
        console.warn("Error while fetching pinned pages...");
      }
    } catch (error) {
      console.error("Failed to fetch pinned pages list");
    }
  };

  const fetchBannerDetails = async () => {
    try {
      const response = await axios.get(
        "https://website-cms-backend-prod-154625965269.asia-south1.run.app/homepage?vertical=Accounting",
      );
      const { status, data } = response;
      if (status === 200) {
        setBannerDetails(data);
      } else {
        console.warn("Data is not found");
      }
    } catch (error) {
      console.error("Failed to fetch banner details");
    }
  };

  useEffect(() => {
    if (categorySlug && tagSlug) {
      fetchPinnedPages(categorySlug, tagSlug);
    }
    fetchPageDetails();
    if (categorySlug) {
      fetchTags(categorySlug);
    }
  }, [tagSlug, pageSlug, categorySlug]);

  useEffect(() => {
    fetchBannerDetails();
  }, []);

  useEffect(() => {
    if (
      categorySlug &&
      tagSlug &&
      tagName.length > 0 &&
      !tagName.some((tag) => tag.slug === tagSlug)
    ) {
      router.replace(`/accounting/learn/${categorySlug}`);
    }
  }, [categorySlug, tagSlug, tagName, router]);

  useEffect(() => {
    const urlPath = window.location.pathname.split("/");
    const [category, tag, pageSlug] = urlPath.slice(3);

    setCategorySlug(category);
    setTagSlug(tag);
    setPageSlug(pageSlug);

    if (pageDetails?.name) {
      setActivePage(pageDetails.name);
    } else if (pinnedPages.length > 0) {
      setActivePage(pinnedPages[0].name);
    }
  }, [pageDetails, pinnedPages]);

  const sanitizedSideBannerHTML = bannerDetails?.side_content
    ? DOMPurify.sanitize(bannerDetails?.side_content)
    : "";

  const renderSideBannerContent = () => {
    if (!bannerDetails || bannerDetails.side_content === "<p>&nbsp;</p>") {
      return (
        <div className="text-center text-3xl text-gray-500 font-bold capitalize p-8">
          No Content available
        </div>
      );
    }

    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizedSideBannerHTML }}
      />
    );
  };

  const handleResize = () => {
    if (window.innerWidth < 768) {
      setIsMobile(true);
    }
  };

  const getYouTubeEmbedURL = (url: string) => {
    const videoId = url.split("v=")[1]?.split("&")[0];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const getDisplayDate = () => {
    if (!pageDetails?.created_at || !pageDetails?.updated_at) return null;

    const createdAt = new Date(pageDetails.created_at);
    const updatedAt = new Date(pageDetails.updated_at);

    const formatDate = (date: Date) =>
      new Intl.DateTimeFormat("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);

    if (updatedAt > createdAt) {
      return `Updated At: ${formatDate(updatedAt)}`;
    }
    return `Created At: ${formatDate(createdAt)}`;
  };

  const convertSlug = (slug: string) => {
    const parts = slug?.split("-");
    if (!parts || parts.length === 0) return "";
    const firstPart = parts[0].toUpperCase();
    const restParts = parts
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `${firstPart} ${restParts}`.trim();
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile]);

  const renderContent = () => {
    if (status === 204) {
      return (
        <p className="text-3xl font-semibold text-[#A0A0A0] text-center">
          No data available for the pinned page.
        </p>
      );
    }

    if (pinnedPageDetails) {
      return (
        <CMSPageParseDescriptionExtend
          cmsContentData={pinnedPageDetails?.content ?? ""}
        />
      );
    }

    return (
      <CMSPageParseDescriptionExtend
        cmsContentData={pageDetails?.content ?? ""}
      />
    );
  };

  return (
    <>
      <div className="mx-auto w-full xl:px-0 md:px-6 lg:!px-6 bg-[#f7f8f9] px-0 relative md:bg-bottom bg-center md:pb-0 pb-[0px]">
        <div className="lg:container mx-auto 2xl:px-0 xl:px-10 lg:p-0 text-[#030F1C] z-20 relative">
          <div className="rounded-[4px] border-[#959595] border-[1px] bg-white md:static sticky top-[68px] z-[1]">
            <div className=" px-3 py-4 border-[#959595] border-b-[1px] relative">
              <div className="flex justify-between items-start ">
                <div className="flex w-full justify-between">
                  <div className="flex items-center">
                    <Link href="/">
                      <HomeIcon className="w-[22px] h-[22px] text-[#3C86F1] cursor" />
                    </Link>
                    <ChevronRightIcon />
                    <Link
                      href="/accounting/learn"
                      className="text-[#3C86F1] 2xl:text-[14px] text-[12px]"
                    >
                      {isMobile
                        ? categorySlug?.length > 15
                          ? `${convertSlug(categorySlug)?.slice(0, 15)}...`
                          : convertSlug(categorySlug)
                        : convertSlug(categorySlug)}
                    </Link>
                    <ChevronRightIcon />
                    <Link
                      href="/accounting/learn"
                      className=" 2xl:text-[14px] text-[12px]"
                    >
                      {isMobile
                        ? tagName[0]?.name?.length > 15
                          ? `${tagName[0]?.name?.slice(0, 15)}...`
                          : tagName[0]?.name
                        : tagName[0]?.name || ""}
                    </Link>
                    <ChevronRightIcon />
                    <Link
                      href="/accounting/learn"
                      className=" 2xl:text-[14px] text-[12px]"
                    >
                      {isMobile
                        ? (pageDetails?.name?.length ?? 0) > 15
                          ? `${pageDetails?.name?.slice(0, 15)}...`
                          : pageDetails?.name || ""
                        : pageDetails?.name || ""}
                    </Link>
                  </div>
                  {/* <button
                    className="px-6 py-2 bg-[#3C86F1] hidden md:block text-white rounded-[20px_20px_20px_0]"
                    onClick={handleDownload}
                  >
                    <span className="flex items-center gap-3 ">
                      <DownloadIcon className="text-white w-[15] h-[15px]" />
                      Download 
                    </span>
                  </button> */}
                </div>
              </div>
              <h1 className="font-bold md:text-[32px] text-[24px] mt-3 mb-1 capitalize md:w-full">
                {pageDetails?.title}
              </h1>
              <p className="text-[20px] font-medium md:block hidden capitalize">
                {pageDetails?.subtitle}
              </p>
              <div className="flex items-center justify-between">
                <p className="text-[#8c8c8c] text-sm">{getDisplayDate()}</p>
              </div>
            </div>
            <div className="overflow-hidden ">
              <ul className="flex justify-start gap-4 text-[#A0A0A0] 2xl:text-[16px] flex-nowrap overflow-x-scroll text-[14px] px-3 font-semibold scroll_cms">
                {pinnedPages.map((pinnedPage, index) => (
                  <li
                    key={index}
                    className={`py-3 px-2 cursor-pointer whitespace-nowrap text-ellipsis capitalize ${
                      activePage === pinnedPage.name ? "active_cms_btn" : ""
                    }`}
                    onClick={() => handlePageClick(pinnedPage.name)}
                  >
                    {pinnedPage && typeof pinnedPage.name === "string"
                      ? isMobile
                        ? pinnedPage.name.length > 15
                          ? `${pinnedPage.name.slice(0, 15)}...`
                          : pinnedPage.name
                        : pinnedPage.name
                      : "Invalid Page"}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex justify-between items-start flex-wrap border-b-[1px] border-black pb-12">
            <div className="md:w-[72%] w-full cms-details-page">
              <div className="cms-page-detail-content md:border-[1px] border-[#959595] px-4 py-3 mt-4 rounded-[4px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)] bg-white">
                {renderContent()}
              </div>
            </div>
            {bannerDetails ? (
              <div className="md:w-[24.5%] w-full md:block hidden">
                {bannerDetails.side_video ? (
                  <div className="rounded-[10px] mt-4 relative cursor-pointer">
                    <iframe
                      height="170"
                      src={getYouTubeEmbedURL(bannerDetails.side_video)}
                      title="video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-[4px]"
                    />
                  </div>
                ) : bannerDetails.side_banner ? (
                  <div className="rounded-[10px] mt-6 relative cursor-pointer w-[298px]">
                    <Image
                      src={bannerDetails.side_banner}
                      alt="MBE"
                      className="rounded-[4px]"
                      width={298}
                      height={170}
                    />
                  </div>
                ) : null}

                <div className="cms_card_sha px-3 py-3 mt-6 rounded-[10px]">
                  <p className="text-[#000] text-base font-bold leading-[1.4] border-b-[0.5px] border-[#959595] pb-2 capitalize">
                    {bannerDetails.side_title}
                  </p>
                  <div className="cms_side_banner">
                    {renderSideBannerContent()}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {isPopupOpen.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-1 shadow-lg relative rounded-[14px] md:w-[auto] w-[85%]">
              <button
                onClick={closePopup}
                className="mt-4 bg-red-700 text-white px-3 py-1 rounded absolute right-[-15px] top-[-30px]"
              >
                ✕
              </button>
              <iframe
                className="lg:w-[600px] md:w-[500px] md:h-[280px] rounded-[14px] lg:h-[320px] h-[220px]"
                width="100%"
                height="320"
                src={`https://www.youtube.com/embed/${isPopupOpen.code}`}
                title="YouTube video player"
                allow="accelerometer; autoplay; climd:oard- pb-14write; encrypted-media; gyroscope; picture-in-picture; web-share"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Details;
